import { Fragment, type ReactNode } from 'react';
import { knowledgeSources } from '../../lib/knowledge-base';

// Render a small Markdown subset as React nodes. Article HTML remains text.
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^\s)]+\))/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i} className="rounded bg-slate-100 px-1 text-[0.9em] break-words">{part.slice(1, -1)}</code>;
    const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (match) return /^https?:\/\//i.test(match[2] ?? '')
      ? <a key={i} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-indigo-700 underline underline-offset-2 break-words">{match[1]}</a>
      : <Fragment key={i}>{match[1]}</Fragment>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}

export function KnowledgeContent({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let index = 0;
  while (index < lines.length) {
    const line = lines[index]!.trim();
    if (!line || /^---+$/.test(line)) { index++; continue; }
    const heading = line.match(/^#{1,6}\s+(.+)$/);
    if (heading) { blocks.push(<h3 key={index} className="text-base font-semibold text-slate-900 pt-3 first:pt-0">{inline(heading[1]!)}</h3>); index++; continue; }
    if (/^[-*]\s+/.test(line) || /^\d+\.\s+/.test(line)) {
      const ordered = /^\d+\.\s+/.test(line), start = index, items: ReactNode[] = [];
      const pattern = ordered ? /^\d+\.\s+/ : /^[-*]\s+/;
      while (index < lines.length && pattern.test(lines[index]!.trim())) items.push(<li key={index}>{inline(lines[index++]!.trim().replace(pattern, ''))}</li>);
      blocks.push(ordered ? <ol key={start} className="list-decimal pl-5 space-y-1.5">{items}</ol> : <ul key={start} className="list-disc pl-5 space-y-1.5">{items}</ul>);
      continue;
    }
    const start = index, paragraph: string[] = [];
    while (index < lines.length && lines[index]!.trim() && !/^#{1,6}\s|^[-*]\s|^\d+\.\s/.test(lines[index]!.trim())) paragraph.push(lines[index++]!.replace(/^>\s?/, ''));
    blocks.push(<p key={start} className="whitespace-pre-line">{inline(paragraph.join('\n'))}</p>);
  }
  return <div className="space-y-3 text-sm leading-7 text-slate-700 break-words">{blocks}</div>;
}

export function KnowledgeSources({ sources }: { sources: unknown }) {
  const refs = knowledgeSources(sources);
  if (!refs.length) return <p className="text-xs text-amber-800">Bij dit artikel zijn de bronnen nog niet vastgelegd.</p>;
  return <ul className="space-y-2 text-xs text-slate-500">{refs.map((source, i) => <li key={`${source.ref}-${i}`} className="break-words">{/^https?:\/\//i.test(source.ref)
    ? <a href={source.ref} target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:underline">{source.label || source.ref}</a>
    : <span title={source.ref}>{source.label || source.ref}</span>}</li>)}</ul>;
}
